import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { client } from '../lib/sanity'
import { Post } from '../types/sanity'
import BlogPostCard from '../components/BlogPostCard'
import Hero from '../components/Hero'
import TherapyTypes from '../components/TherapyTypes'
import DonationCTA from '../components/DonationCTA'
import Map from '../components/Map'
import LogoTicker from '../components/LogoTicker'

export default function Home() {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const query = `*[_type == "post"] | order(publishedAt desc)[0...3] {
      _id,
      title,
      description,
      readTime,
      slug,
      author->{
        _id,
        name,
        slug,
        image
      },
      mainImage,
      publishedAt,
      categories[]->{
        _id,
        title
      }
    }`

    client.fetch(query)
      .then((data: Post[]) => {
        setPosts(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching posts:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const [featuredPost, ...remainingPosts] = posts;

  return (
    <article>
      {/* Hero Section */}
      <Hero 
        title="Educación y rehabilitación preescolar inclusiva para niños y niñas. Con y sin discapacidad."
        backgroundImage="/images/banners/kinder1_gaviota.jpg"
      />

      {/* Blog Preview Section */}
      <div className="w-screen mt-[60px] md:mt-[120px] mb-16 md:mb-32">
        <div className="w-full px-4 md:px-8 md:max-w-[1280px] md:mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-12">
            <h2 className="headline-large mb-4 md:mb-0">¡Infórmate!</h2>
            <Link to="/blog" className="body-small text-black hover:text-gray-600 transition-colors">
              Ver todo el blog →
            </Link>
          </div>
          
          {error && (
            <div className="text-center py-8 text-red-600">
              Error loading posts: {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-8">Loading posts...</div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="text-center py-8">No posts found.</div>
          )}

          {!loading && !error && posts.length > 0 && (
            <>
              {/* Featured Post */}
              <div className="mb-6 md:mb-8">
                <BlogPostCard post={featuredPost} featured={true} />
              </div>

              {/* Regular Posts Grid */}
              {remainingPosts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 w-full">
                  {remainingPosts.map((post) => (
                    <BlogPostCard key={post._id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Decorative Banner */}
      <div
        className="w-screen h-[50vh] md:h-[75vh] mb-8 md:mb-16"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundImage: 'url("/images/banners/panaderia_gaviota.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          backgroundRepeat: 'no-repeat'
        }}
        role="img"
        aria-label="Decorative Banner"
      />

      {/* Therapy Types Section */}
      <TherapyTypes />

      {/* Donation CTA Section */}
      <DonationCTA />

      {/* Map Section */}
      <div className="mt-16 md:mt-32">
        <Map />
      </div>

      {/* Logo Ticker Section */}
      <div className="mb-16 md:mb-32">
        <LogoTicker />
      </div>
    </article>
  )
} 